import { Moment } from "moment-timezone";
import { useCallback, useMemo } from "react";
import { useMatches } from "react-router";
import { ApolloLink, OperationVariables } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { useIntl } from "react-intl";
import isoCountries from "i18n-iso-countries";
import { AnyPeriod, AnyPrice, castPeriod, castPrice, Price, toGQLPrice, useAppConfig } from "@ct-react/core";
import { useLocaleContext, useLocaleFormatter } from "@ct-react/locale";
import { getRecaptchaToken } from "../bundles/client-ui";
import { BasicPrice, RentalPrice } from "@shared/graphql";
import { pricing } from "../i18n/sharable-defs";

export const useTools = () => {

  const { options: { recaptchaSiteKey } } = useAppConfig();
  const intl = useIntl();
  const { locale } = useLocaleContext();
  const matchedRoutes = useMatches();
  const { print } = useLocaleFormatter();

  const routingData = useMemo(() => (key: "search" | "page"): any | undefined => {
    const matches = matchedRoutes?.filter(r => !!r.loaderData && key in (r.loaderData as any));
    if (!matches) return undefined;
    return matches.reduce((acc, cur) => ({...acc, ...(cur.loaderData as any)[key] }), {});
  }, [ matchedRoutes ]);

  const nights = useCallback((period: AnyPeriod) => {
    const casted = castPeriod(period);
    return casted.end.diff(casted.start, "day");
  }, []);

  const sumPrices = useCallback((prices: AnyPrice[]): AnyPrice => {
    const result = prices.reduce((acc: Price, cur: AnyPrice) => {
      const current = castPrice(cur);
      return { amount: (acc.amount || 0) + current.amount, currency: current.currency };
    }, {} as Price);
    return toGQLPrice(result);
  }, []);

  const anyPrice = useCallback((price?: BasicPrice | RentalPrice) => {
    if (!price || price.onDemand) return intl.formatMessage(pricing.onDemand);
    const formatted = print.price(price.value, { trailingZeroDisplay: "stripIfInteger" });
    return "type" in price
      ? intl.formatMessage(pricing.lowest, { type: price.type, price: formatted })
      : formatted
  }, []);

  const countries = useMemo(() => Object.fromEntries(Object
    .entries(isoCountries.getNames(locale.basename, { select: "official" }))
    .sort((a, b) => a[1].localeCompare(b[1]))), [ locale ]);

  const labelizeCalendar = useMemo(() => ({
    month: (m: Moment) => intl.formatDate(m.toDate(), { month: "long", year: "numeric" }),
    dayOfWeek: (d: Moment) => intl.formatDate(d.toDate(), { weekday: "short" }),
    onlineRestricted: intl.formatMessage(pricing.onDemandShort)
  }), [ intl ]);

  const withRecaptcha = <TData = any, TVariables extends OperationVariables = OperationVariables>(
    mutation: (options?: useMutation.MutationFunctionOptions<TData, TVariables>) => Promise<ApolloLink.Result<TData>>,
    action?: string) => {
    return async (options?: useMutation.MutationFunctionOptions<TData, TVariables>): Promise<ApolloLink.Result<TData>> => {
      if (!recaptchaSiteKey)
        return mutation(options);

      const token = await getRecaptchaToken(recaptchaSiteKey, !!action ? { action } : undefined);
      return mutation({
        ...options,
        context: {
          ...options?.context,
          headers: { ...options?.context?.headers, "GQL-Consumer-Recaptcha-Token": token }
        }
      });
    }
  }

  return {
    routingData,
    format: { nights, anyPrice, sumPrices },
    countries,
    labelizeCalendar,
    mutation: { withRecaptcha }
  };

}
